import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import campaignApi from "../api/campaignApi";
import scenarioApi from "../api/scenarioApi";
import { showError, showSuccess } from "../stores/alert/slice.alert";
import { closeLoading, openLoading } from "../stores/loading/slice.loading";
import moment from "moment";
import PropTypes from "prop-types";
import types from "../constants/types";
import styles from "../views/Campaign/CampaignView.module.scss";
import cn from "classnames";

/* ── Status meta (chỉ giữ cls, label sẽ lấy qua t() trong component) ── */
const STATUS_META_CLS = {
  init:      styles.statusInit,
  pending:   styles.statusInit,
  running:   styles.statusSending,
  sending:   styles.statusSending,
  scheduled: styles.statusScheduled,
  done:      styles.statusDone,
  failed:    styles.statusFailed,
  cancel:    styles.statusPaused,
  paused:    styles.statusPaused,
};

const SC_STATUS_META_CLS = {
  init:    styles.statusInit,
  sending: styles.statusSending,
  paused:  styles.statusPaused,
  done:    styles.statusDone,
  failed:  styles.statusFailed,
};

/* ── Helpers ─────────────────────────────────────────────── */
function fmt(n) {
  if (n === null || n === undefined) return "0";
  return Number(n).toLocaleString("vi-VN");
}
function fmtDate(iso) {
  if (!iso) return "—";
  return moment(iso).format("DD/MM/YYYY");
}
function fmtTime(iso) {
  if (!iso) return "";
  return moment(iso).format("HH:mm");
}
function displayName(c, t) {
  return c.name || `${t('update.campaign')} #${c.code || c.id?.slice(0, 6)}`;
}
function shortUuid(u) {
  if (!u) return "";
  return u.slice(0, 8) + "…";
}
function isOverdue(s) {
  if (!s.timeSchedule) return false;
  const now = new Date();
  return (
    new Date(s.timeSchedule) < now &&
    s.status !== "done" &&
    s.status !== "sending"
  );
}
function overdueBy(iso, t) {
  const days = Math.floor((new Date() - new Date(iso)) / 86400000);
  return days < 1 ? t('campaign_page.overdue_today') : t('campaign_page.overdue_days', { count: days });
}
function relFuture(iso, t) {
  const days = Math.ceil((new Date(iso) - new Date()) / 86400000);
  if (days <= 0) return t('campaign_page.rel_today');
  if (days === 1) return t('campaign_page.rel_tomorrow');
  return t('campaign_page.rel_in_days', { count: days });
}

/* ── Stat Cell ───────────────────────────────────────────── */
function StatCell({ total = 0, success = 0, fail = 0, unit }) {
  const pending = Math.max(0, total - success - fail);
  if (success === 0 && fail === 0) {
    return (
      <div className={styles.statCell}>
        <span className={styles.statMuted}>
          <span className={styles.statMutedBold}>{fmt(total)}</span>
          {" "}{unit}
        </span>
      </div>
    );
  }
  const pctS = total > 0 ? ((success / total) * 100).toFixed(1) : "0";
  const pctF = total > 0 ? ((fail / total) * 100).toFixed(1) : "0";
  return (
    <div className={styles.statCell}>
      <span className={styles.statTotal}>
        {fmt(total)} <span className={styles.statTotalSmall}>{unit}</span>
      </span>
      <div className={styles.statBar}>
        <span className={styles.sbOk} style={{ width: pctS + "%" }} />
        <span className={styles.sbFail} style={{ width: pctF + "%" }} />
      </div>
      <div className={styles.statNums}>
        <span className={styles.snOk}>✓ {fmt(success)}</span>
        <span className={styles.snFail}>✕ {fmt(fail)}</span>
        <span>◌ {fmt(pending)}</span>
      </div>
    </div>
  );
}

/* ── Sched Cell ──────────────────────────────────────────── */
function SchedCell({ sc, t, campaignId, pageId }) {
  const rawScheduleTime = sc.schedule?.scheduleTime;
  const scheduleTime = Number(rawScheduleTime);
  const hasSchedule = rawScheduleTime !== null && rawScheduleTime !== undefined && Number.isFinite(scheduleTime);
  const openScheduleModal = (e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token') || null;
    const url = `${types.URL.FACEBOOK}&token=${token}&tab=schedule&scenarioId=${sc.id}&campaign=${campaignId}&pageId=${pageId}`;
    window.open(url, '_blank');
  };

  if (!hasSchedule) {
    return (
      <button className={styles.btnSchedInline} onClick={openScheduleModal}>
        <svg viewBox="0 0 14 14" fill="none">
          <rect x="1.5" y="2.5" width="11" height="10" rx="1" />
          <path d="M4.5 2.5V1M9.5 2.5V1M1.5 5.5h11M7 8v2.5M5.8 9.2h2.4" />
        </svg>
        {t('campaign_page.sched_set')}
      </button>
    );
  }

  const scenarioWithScheduleTime = { ...sc, timeSchedule: Number(scheduleTime) };

  if (isOverdue(scenarioWithScheduleTime)) {
    return (
      <div className={styles.schedCell}>
        <span className={styles.schedOverdue}>
          <svg viewBox="0 0 14 14" fill="none">
            <path d="M7 1.5L1 12h12L7 1.5z" /><path d="M7 5.5v3M7 10.5h.01" />
          </svg>
          {overdueBy(scheduleTime, t)}
        </span>
        <span className={styles.schedSub}>
          {t('campaign_page.sched_at')} {fmtTime(scheduleTime)} · {fmtDate(scheduleTime)}
        </span>
      </div>
    );
  }
  if (sc.status === "done") {
    return (
      <div className={styles.schedCell}>
        <span className={styles.schedMain}>{t('campaign_page.sched_sent')}</span>
        <span className={styles.schedSub}>
          {fmtTime(scheduleTime)} · {fmtDate(scheduleTime)}
        </span>
      </div>
    );
  }
  if (sc.status === "sending") {
    return (
      <div className={styles.schedCell}>
        <span className={styles.schedMain}>{t('campaign_page.sched_sending')}</span>
        <span className={styles.schedSub}>
          {t('campaign_page.sched_started')} {fmtTime(scheduleTime)} · {fmtDate(scheduleTime)}
        </span>
      </div>
    );
  }
  return (
    <div className={styles.schedCell}>
      <span className={styles.schedMain}>
        {relFuture(scheduleTime, t)}, {fmtTime(scheduleTime)}
      </span>
      <span className={styles.schedSub}>{fmtDate(scheduleTime)}</span>
    </div>
  );
}

/* ── Scenario Sub-Table ──────────────────────────────────── */
function ScenarioSubTable({ campaignId, pageId, onViewScenario, onDeleteScenario, onCopy, t }) {
  const [scenarios, setScenarios] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleCopySc = (text, e) => {
    e.stopPropagation();
    onCopy(text);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await scenarioApi.getByCampaign(campaignId, {
          page: 0,
          size: 50,
          sort: "createdAt,asc",
        });
        if (!cancelled) {
          setScenarios(res?.content || []);
        }
      } catch {
        if (!cancelled) setScenarios([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId]);

  if (loading) {
    return (
      <tr className={styles.subLoadingRow}>
        <td colSpan={7}><span className={styles.subLoading}>{t('campaign_page.loading_scenarios')}</span></td>
      </tr>
    );
  }

  const doneCount = (scenarios || []).filter((s) => s.status === "done").length;

  return (
    <tr className={styles.subRow}>
      <td colSpan={7}>
        <div className={styles.subWrap}>
          <div className={styles.subPanel}>
            <div className={styles.subHead}>
              <span className={styles.subTitle}>
                {t('campaign_page.scenarios_of_campaign')} —{" "}
                <b>{doneCount}/{scenarios?.length || 0} {t('campaign_page.completed')}</b>
              </span>
              <button
                className={styles.btnAddScenario}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `${types.URL.FACEBOOK}&token=${localStorage.getItem("token") || null}&campaign=${campaignId}&pageId=${pageId}`,
                    "_blank"
                  );
                }}
              >
                <svg viewBox="0 0 14 14" fill="none">
                  <path d="M7 2v10M2 7h10" />
                </svg>
                {t('campaign_page.add_scenario')}
              </button>
            </div>
            <div className={styles.tblScroll}>
              <table className={styles.subTable}>
                <thead>
                  <tr>
                    <th>{t('campaign_page.col_scenario')}</th>
                    <th>{t('campaign_page.col_status')}</th>
                    <th>{t('campaign_page.col_result')}</th>
                    <th>{t('campaign_page.col_schedule')}</th>
                    <th className={styles.thRight}>{t('campaign_page.col_action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!scenarios?.length ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "20px", color: "#9CA3AF" }}>
                        {t('campaign_page.no_scenarios')}
                      </td>
                    </tr>
                  ) : (
                    scenarios.map((sc) => {
                      const metaCls = SC_STATUS_META_CLS[sc.status] || SC_STATUS_META_CLS.init;
                      const scStatusLabel = {
                        init: t('campaign_page.status_init'),
                        sending: t('campaign_page.status_sending'),
                        paused: t('campaign_page.status_paused'),
                        done: t('campaign_page.status_done'),
                        failed: t('campaign_page.status_failed'),
                      }[sc.status] || t('campaign_page.status_init');
                      const ok    = sc.customerSuccessAll ?? sc.numberCusSuccess ?? sc.customerSuccess ?? sc.totalCustomersDone ?? 0;
                      const fail  = sc.customerFailAll ?? sc.numberCusFailed ?? sc.customerFailed ?? sc.totalCustomersError ?? 0;
                      const total = sc.customerAll ?? sc.totalCus ?? sc.numberCus ?? 0;
                      return (
                        <tr
                          key={sc.id}
                          className={styles.scRow}
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewScenario(campaignId, sc.id, pageId);
                          }}
                        >
                          <td>
                            <div className={styles.scCell}>
                              <span className={styles.scName} title={sc.name || sc.id}>
                                {sc.name || `${t('campaign_page.col_scenario')} #${sc.id?.slice(0, 8)}`}
                              </span>
                              <div className={styles.campaignCodeRow}>
                                <span className={styles.scCode} title={sc.id}>{shortUuid(sc.id)}</span>
                                <button
                                  className={styles.copyBtn}
                                  title={t('common.btn.copy')}
                                  onClick={(e) => handleCopySc(sc.id, e)}
                                >
                                  <svg viewBox="0 0 12 12" fill="none">
                                    <rect x="3" y="3" width="7" height="7" rx="1" />
                                    <path d="M2 8V2h6" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={cn(styles.statusBadge, metaCls)}>
                              <span className={styles.statusDot} />
                              {scStatusLabel}
                            </span>
                          </td>
                          <td>
                            <StatCell total={total} success={ok} fail={fail} unit={t('campaign_page.unit_customer')} />
                          </td>
                          <td><SchedCell sc={sc} t={t} campaignId={campaignId} pageId={pageId} /></td>
                          <td className={styles.tdRight}>
                            <div className={styles.actionCell}>
                              {sc.status === "init" && (
                                <button
                                  className={cn(styles.btnAct, styles.send)}
                                  onClick={(e) => { e.stopPropagation(); onViewScenario(campaignId, sc.id, pageId); }}
                                >
                                  <svg viewBox="0 0 14 14" fill="none"><path d="M1.5 7l11-5.5-3.5 11-2.5-4.5-5-1z" /></svg>
                                  {t('campaign_page.btn_send_now')}
                                </button>
                              )}
                              {sc.status === "paused" && (
                                <button
                                  className={cn(styles.btnAct, styles.resume)}
                                  onClick={(e) => { e.stopPropagation(); onViewScenario(campaignId, sc.id, pageId); }}
                                >
                                  <svg viewBox="0 0 14 14" fill="none"><path d="M4 2.5l8 4.5-8 4.5v-9z" /></svg>
                                  {t('campaign_page.btn_resume')}
                                </button>
                              )}
                              {(sc.status === "done" || sc.status === "sending") && (
                                <button
                                  className={cn(styles.btnAct, styles.report)}
                                  onClick={(e) => { e.stopPropagation(); onViewScenario(campaignId, sc.id, pageId); }}
                                >
                                  <svg viewBox="0 0 14 14" fill="none"><path d="M2 12V6M7 12V2M12 12V8" /></svg>
                                  {sc.status === "done" ? t('campaign_page.btn_report') : t('campaign_page.btn_monitor')}
                                </button>
                              )}
                              <button
                                className={styles.iconBtn}
                                title={t('campaign_page.btn_delete_scenario')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteScenario({
                                    campaignId,
                                    scenarioId: sc.id,
                                    name: sc.name || `${t('campaign_page.col_scenario')} #${sc.id?.slice(0, 8)}`,
                                  });
                                }}
                              >
                                <svg viewBox="0 0 16 16" fill="none">
                                  <path d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9.5h6.6L12 4M6.5 7v4M9.5 7v4" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

/* ── Main Component ──────────────────────────────────────── */
export default function ListCampaign({ data, getCampaign }) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const rows = (data && data.content) || [];
  const totalElements = data?.totalElements || 0;
  const totalPages = data?.totalPages || 1;

  // ── Filter State ─────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortOrder, setSortOrder] = useState("createdAt,desc");
  const [activeStatus, setActiveStatus] = useState("all");
  const debounceRef = useRef(null);
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimer = useRef(null);

  // ── Delete confirm state ──────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState(null);
  // { type: 'campaign', id, name } | { type: 'scenario', campaignId, scenarioId, name }

  // ── Expanded rows ─────────────────────────────────────────
  const [expanded, setExpanded] = useState(new Set());

  // ── Status counts — fetch once per search/date filter change ──
  const [statusCounts, setStatusCounts] = useState({});
  const statusCountsRef = useRef({});
  // Lưu riêng tổng "Tất cả" — không bị ghi đè khi filter theo status
  const [totalAll, setTotalAll] = useState(0);

  const STATUS_LIST = ["all", "init", "sending", "scheduled", "done", "paused"];
  const STATUS_LABELS = {
    all: t('campaign_page.status_all'),
    init: t('campaign_page.status_init'),
    sending: t('campaign_page.status_sending'),
    scheduled: t('campaign_page.status_scheduled'),
    done: t('campaign_page.status_done'),
    failed: t('campaign_page.status_failed'),
    paused: t('campaign_page.status_paused'),
  };

  // ── API calls ─────────────────────────────────────────────
  const buildParams = useCallback((
    q = search, p = page, size = rowsPerPage,
    sd = startDate, ed = endDate, sort = sortOrder, status = activeStatus,
    withStatistic = false
  ) => {
    let searchStr = "";
    if (status && status !== "all") {
      searchStr += `status==${status === "running" ? "sending" : status};`;
    }
    if (sd && ed) searchStr += `createdAt=bw=${sd},${ed};`;
    if (q && q.trim()) {
      const trimmed = q.trim();
      const isUUID = /^[0-9a-fA-F-]{36}$/.test(trimmed);
      searchStr += isUUID ? `id==${trimmed};` : `name|pageNameText|id==${trimmed};`;
    }
    const params = { page: p, size, search: searchStr, sort };
    if (withStatistic) params.includeStatistic = true;
    return params;
  }, [search, page, rowsPerPage, startDate, endDate, sortOrder, activeStatus]);

  const fetchData = useCallback(async (overrides = {}) => {
    try {
      const params = buildParams(
        overrides.q ?? search, overrides.p ?? page, overrides.size ?? rowsPerPage,
        overrides.sd ?? startDate, overrides.ed ?? endDate,
        overrides.sort ?? sortOrder, overrides.status ?? activeStatus,
        overrides.includeStatistic === true
      );

      getCampaign(params);

      console.log('[ListCampaign] Fetching data with params:', params);
    } catch (err) {
      console.error('[ListCampaign] Failed to fetch data:', err);
      const fallback = { all: 0, init: 0, sending: 0, scheduled: 0, done: 0, paused: 0 };
      statusCountsRef.current = fallback;
      setTotalAll(0);
      setStatusCounts(fallback);
    }
  }, [buildParams, search, page, rowsPerPage, startDate, endDate, sortOrder, activeStatus, getCampaign]);

  // Chỉ cập nhật chip khi response có statistic (lần load đầu)
  useEffect(() => {
    const stats = data?.statistic;
    if (!stats) return;

    const counts = {
      all: stats.totalCampaigns ?? data?.totalElements ?? 0,
      init: stats.pendingCampaigns ?? 0,
      sending: stats.runningCampaigns ?? 0,
      scheduled: stats.scheduledCampaigns ?? 0,
      done: stats.doneCampaigns ?? 0,
      paused: stats.cancelCampaigns ?? 0,
    };

    statusCountsRef.current = counts;
    setTotalAll(counts.all);
    setStatusCounts(counts);

    console.log('[ListCampaign] Statistics updated from data:', counts);
  }, [data]);

  useEffect(() => { fetchData({ includeStatistic: true }); }, []); // eslint-disable-line

  // ── Handlers ──────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      fetchData({ q: val, p: 0 });
      // Không cần fetchStatusCounts - statistics đã có sẵn từ initial load
    }, 400);
  };

  const handleDateFrom = (e) => {
    const sd = e.target.value;
    setStartDate(sd);
    setPage(0);
    fetchData({ sd, p: 0 });
    // Không cần fetchStatusCounts
  };

  const handleDateTo = (e) => {
    const ed = e.target.value;
    setEndDate(ed);
    setPage(0);
    fetchData({ ed, p: 0 });
    // Không cần fetchStatusCounts
  };

  const handleSort = (e) => {
    const sort = e.target.value;
    setSortOrder(sort);
    setPage(0);
    fetchData({ sort, p: 0 });
  };

  const handleStatusChip = (status) => {
    setActiveStatus(status);
    setPage(0);
    fetchData({ status, p: 0 });
  };

  const handleClearFilters = () => {
    setSearch(""); setStartDate(""); setEndDate(""); setActiveStatus("all"); setPage(0);
    fetchData({ q: "", sd: "", ed: "", status: "all", p: 0 });
    // Không cần fetchStatusCounts - giữ nguyên statistics ban đầu
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData({ p: newPage });
  };

  const handlePerPage = (e) => {
    const size = +e.target.value;
    setRowsPerPage(size);
    setPage(0);
    fetchData({ size, p: 0 });
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onViewCampaign = useCallback((campaignId, pageId) => {
    window.open(
      `${types.URL.FACEBOOK}&token=${localStorage.getItem("token") || null}&campaign=${campaignId}&pageId=${pageId}`,
      "_blank"
    );
  }, []);

  const onViewScenario = useCallback((campaignId, scenarioId, pageId) => {
    window.open(
      `${types.URL.FACEBOOK}&token=${localStorage.getItem("token") || null}&campaign=${campaignId}&pageId=${pageId}&scenario=${scenarioId}`,
      "_blank"
    );
  }, []);

  const handleCopy = (text, e) => {
    e.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(String(text));
      setToastMsg(t('campaign_page.copy_success', { id: text }));
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToastMsg(null), 2500);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      dispatch(openLoading());
      if (confirmDelete.type === 'campaign') {
        await campaignApi.delete(confirmDelete.id);
        dispatch(showSuccess(t('campaign_page.delete_campaign_success')));
        fetchData();
      } else if (confirmDelete.type === 'scenario') {
        await scenarioApi.deleteScenario({
          id: confirmDelete.campaignId,
          scenarioId: confirmDelete.scenarioId,
        });
        dispatch(showSuccess(t('campaign_page.delete_scenario_success')));
        setExpanded((prev) => {
          const next = new Set(prev);
          next.delete(confirmDelete.campaignId);
          return next;
        });
        setTimeout(() => {
          setExpanded((prev) => new Set([...prev, confirmDelete.campaignId]));
        }, 100);
      }
    } catch (err) {
      dispatch(showError(err?.message || t('update.error_msg')));
    } finally {
      dispatch(closeLoading());
      setConfirmDelete(null);
    }
  };

  const hasFilter = activeStatus !== "all" || search || startDate || endDate;

  // ── Pagination ────────────────────────────────────────────
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const currentPage = page + 1;
    let pages = [];
    if (totalPages <= 7) {
      pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      pages = [1];
      if (currentPage > 3) pages.push("…");
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("…");
      pages.push(totalPages);
    }
    return (
      <div className={styles.pagination}>
        <button className={styles.pgBtn} disabled={page === 0} onClick={() => handlePageChange(page - 1)}>‹</button>
        {pages.map((p, i) =>
          p === "…"
            ? <span key={i} className={styles.pgEllipsis}>…</span>
            : <button key={i} className={cn(styles.pgBtn, { [styles.on]: p === currentPage })} onClick={() => handlePageChange(p - 1)}>{p}</button>
        )}
        <button className={styles.pgBtn} disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)}>›</button>
      </div>
    );
  };

  // ── Action Cell ───────────────────────────────────────────
  const renderActionCell = (row) => {
    const status = row.status;
    const canDetail = ["sending", "done"].includes(status);
    const canSend   = ["init", "scheduled"].includes(status);
    const canResume = status === "paused";
    const canResend = status === "failed";
    return (
      <div className={styles.actionCell}>
        {canDetail && (
          <button className={styles.btnDetail} onClick={(e) => { e.stopPropagation(); onViewCampaign(row.id, row.pageId); }}>
            <svg viewBox="0 0 14 14" fill="none"><path d="M2 12V6M7 12V2M12 12V8" /></svg>
            {t('campaign_page.btn_detail')}
          </button>
        )}
        {canSend && (
          <button className={styles.btnSendNow} onClick={(e) => { e.stopPropagation(); onViewCampaign(row.id, row.pageId); }}>
            <svg viewBox="0 0 14 14" fill="none"><path d="M2 7l10-5-3.5 10L6 8 2 7z" /></svg>
            {t('campaign_page.btn_send_now')}
          </button>
        )}
        {canResume && (
          <button className={styles.btnSendNow} onClick={(e) => { e.stopPropagation(); onViewCampaign(row.id, row.pageId); }}>
            <svg viewBox="0 0 14 14" fill="none"><path d="M4 2.5l8 4.5-8 4.5v-9z" /></svg>
            {t('campaign_page.btn_resume')}
          </button>
        )}
        {canResend && (
          <button className={cn(styles.btnSendNow, styles.danger)} onClick={(e) => { e.stopPropagation(); onViewCampaign(row.id, row.pageId); }}>
            <svg viewBox="0 0 14 14" fill="none"><path d="M12 7A5 5 0 112 7m0 0V3.5M2 7h3.5" /></svg>
            {t('campaign_page.btn_resend')}
          </button>
        )}
        <button className={styles.iconBtn} title={t('campaign_page.btn_delete_campaign')} onClick={(e) => {
          e.stopPropagation();
          setConfirmDelete({ type: 'campaign', id: row.id, name: displayName(row, t) });
        }}>
          <svg viewBox="0 0 16 16" fill="none">
            <path d="M2.5 4.5h11M6.5 2h3M5.5 4.5V13a1 1 0 001 1h3a1 1 0 001-1V4.5M6.8 7v4.5M9.2 7v4.5" />
          </svg>
        </button>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <>
      {/* TOOLBAR */}
      <div className={styles.toolbar}>
        <div id="search_campaign" className={styles.searchWrap}>
          <svg viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.5" /><path d="M13.5 13.5L10.3 10.3" />
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder={t('campaign_page.search_placeholder')}
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <div className={styles.toolbarSpacer} />
        <div className={styles.dateRange}>
          <input type="date" value={startDate} onChange={handleDateFrom} />
          <span className={styles.dateRangeSep}>{t('campaign_page.date_to')}</span>
          <input type="date" value={endDate} onChange={handleDateTo} />
        </div>
        <select id="sort_campaign" className={styles.selectSort} value={sortOrder} onChange={handleSort}>
          <option value="createdAt,desc">{t('campaign_page.sort_newest')}</option>
          <option value="createdAt,asc">{t('campaign_page.sort_oldest')}</option>
          <option value="name,asc">{t('campaign_page.sort_name_az')}</option>
        </select>
      </div>

      {/* FILTER CHIPS */}
      <div id="filter_campaign" className={styles.filterRow}>
        {STATUS_LIST.map((k) => {
          let count;
          if (k === 'all') count = totalAll || totalElements;
          else if (activeStatus === k) count = totalElements;
          else count = statusCounts[k] ?? '…';

          return (
            <div
              key={k}
              className={cn(styles.chip, { [styles.on]: activeStatus === k })}
              onClick={() => handleStatusChip(k)}
            >
              {STATUS_LABELS[k]} <span className={styles.chipCount}>{count}</span>
            </div>
          );
        })}
        <button
          className={cn(styles.filterClear, { [styles.show]: hasFilter })}
          onClick={handleClearFilters}
        >
          {t('campaign_page.filter_clear')}
        </button>
      </div>

      {/* TABLE */}
      <div className={styles.tblScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('campaign_page.col_campaign')}</th>
              <th>{t('campaign_page.col_fanpage')}</th>
              <th>{t('campaign_page.col_status')}</th>
              <th>{t('campaign_page.col_customers')}</th>
              <th>{t('campaign_page.col_messages')}</th>
              <th>{t('campaign_page.col_time')}</th>
              <th className={styles.thRight}>{t('campaign_page.col_action')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className={styles.tblEmpty}>
                    <div className={styles.tblEmptyIcon}>
                      <svg viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="7" /><path d="M10 6.5v4l2.5 1.5" />
                      </svg>
                    </div>
                    <div className={styles.tblEmptyTitle}>{t('campaign_page.empty_title')}</div>
                    <div className={styles.tblEmptySub}>{t('campaign_page.empty_sub')}</div>
                    <button
                      className={styles.btnCreateSm}
                      onClick={() => window.open(`${types.URL.FACEBOOK}&token=${localStorage.getItem("token") || null}`, "_blank")}
                    >
                      <svg viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" /></svg>
                      {t('common.btn.create_campaign')}
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const metaCls = STATUS_META_CLS[row.status] || STATUS_META_CLS.init;
                const statusLabel = STATUS_LABELS[row.status] || STATUS_LABELS.init;
                const isOpen = expanded.has(row.id);

                const custTotal       = row.totalCus || 0;
                const custSuccess     = row.totalCustomersDone || 0;
                const custFail        = row.totalCustomersError || 0;
                const msgTotal        = row.totalMes || 0;
                const msgSuccess      = row.totalSuccess || 0;
                const msgFail         = row.totalFail || 0;
                const scenarioCount   = row.totalScenario ?? 0;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={cn(styles.campaignRow, { [styles.expanded]: isOpen })}
                      onClick={() => toggleExpand(row.id)}
                    >
                      <td>
                        <div className={styles.campaignTd}>
                          <button className={styles.expandChevron} title={isOpen ? t('campaign_page.collapse') : t('campaign_page.view_scenarios')}>
                            <svg viewBox="0 0 12 12" fill="none"><path d="M4 2l4 4-4 4" /></svg>
                          </button>
                          <div className={styles.campaignCell}>
                            <span className={styles.campaignName} title={displayName(row, t)}>
                              {displayName(row, t)}
                            </span>
                            <div className={styles.campaignCodeRow}>
                              <span className={styles.campaignCode} title={row.id}>
                                {shortUuid(row.id)}
                              </span>
                              <button
                                className={styles.copyBtn}
                                title={t('common.btn.copy')}
                                onClick={(e) => handleCopy(row.id, e)}
                              >
                                <svg viewBox="0 0 12 12" fill="none">
                                  <rect x="3" y="3" width="7" height="7" rx="1" />
                                  <path d="M2 8V2h6" />
                                </svg>
                              </button>
                            </div>
                            <span className={styles.scenarioCountBadge}>
                              {scenarioCount} {t('campaign_page.col_scenario')}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles.pageCell}>
                          <span className={styles.pageName}>{row.pageNameText || row.pageName || "—"}</span>
                          <span className={styles.pageOwner}>{row.userCreatedName || ""}</span>
                        </div>
                      </td>
                      <td>
                        <span className={cn(styles.statusBadge, metaCls)}>
                          <span className={styles.statusDot} />
                          {statusLabel}
                        </span>
                      </td>
                      <td>
                        <StatCell total={custTotal} success={custSuccess} fail={custFail} unit={t('campaign_page.unit_customer')} />
                      </td>
                      <td>
                        <StatCell total={msgTotal} success={msgSuccess} fail={msgFail} unit={t('campaign_page.unit_message')} />
                      </td>
                      <td>
                        <div className={styles.timeCell}>
                          <span className={styles.timeMain}>{fmtDate(row.createdAt)}</span>
                          <span className={styles.timeSub}>
                            {t('campaign_page.created_at')} {fmtTime(row.createdAt)}
                            {row.timeStart ? ` · ${t('campaign_page.started_at')} ${fmtDate(row.timeStart)}` : ""}
                          </span>
                        </div>
                      </td>
                      <td className={styles.tdRight}>{renderActionCell(row)}</td>
                    </tr>

                    {isOpen && (
                      <ScenarioSubTable
                        campaignId={row.id}
                        pageId={row.pageId}
                        onViewScenario={onViewScenario}
                        onDeleteScenario={(info) => setConfirmDelete({ type: 'scenario', ...info })}
                        t={t}
                        onCopy={(text) => {
                          if (navigator.clipboard) {
                            navigator.clipboard.writeText(String(text));
                            setToastMsg(t('campaign_page.copy_success', { id: text }));
                            if (toastTimer.current) clearTimeout(toastTimer.current);
                            toastTimer.current = setTimeout(() => setToastMsg(null), 2500);
                          }
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* FOOTER */}
      <div className={styles.tblFooter}>
        <span className={styles.tblCount}>
          {totalElements > 0 && (
            <>
              {t('fanpage_page.per_page')} <b>{page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, totalElements)}</b>{" "}
              / <b>{fmt(totalElements)}</b> {t('campaign_page.col_campaign').toLowerCase()}
            </>
          )}
        </span>
        <div className={styles.perPage}>
          <span>{t('fanpage_page.per_page')}</span>
          <select className={styles.selectSort} value={rowsPerPage} onChange={handlePerPage}>
            <option value={8}>8</option>
            <option value={15}>15</option>
            <option value={30}>30</option>
          </select>
          <span>{t('fanpage_page.per_page_unit')}</span>
        </div>
        {renderPagination()}
      </div>

      {/* Toast Copy */}
      <div className={`${styles.toast} ${toastMsg ? styles.show : ''}`}>
        <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5l3 3 6-6.5" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        {toastMsg}
      </div>

      {/* Confirm Delete Dialog */}
      {confirmDelete && (
        <div className={styles.confirmOverlay} onClick={() => setConfirmDelete(null)}>
          <div className={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.confirmTitle}>
              {t('campaign_page.confirm_delete_title', {
                type: confirmDelete.type === 'campaign'
                  ? t('campaign_page.col_campaign').toLowerCase()
                  : t('campaign_page.col_scenario').toLowerCase()
              })}
            </div>
            <div className={styles.confirmSub}>
              <b>"{confirmDelete.name}"</b> {t('campaign_page.confirm_delete_sub')}
            </div>
            <div className={styles.confirmActions}>
              <button className={styles.confirmCancel} onClick={() => setConfirmDelete(null)}>
                {t('common.btn.cancel')}
              </button>
              <button className={styles.confirmOk} onClick={handleConfirmDelete}>
                {t('common.btn.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

ListCampaign.propTypes = {
  data: PropTypes.object,
  getCampaign: PropTypes.func.isRequired,
  handleGetCampaign: PropTypes.func,
  isShowFilter: PropTypes.bool,
};

ListCampaign.defaultProps = {
  isShowFilter: true,
};
